/**
 * 講師フィードバック画面で使う純粋関数・ロック受付ロジック
 * ユニットテスト可能にするためここに集約
 */

/**
 * UTC時刻を日本時間（JST）の文字列に変換
 * @param {string} utcString - UTC時刻文字列（例: "2026-02-01T05:09:13.261766+00:00"）
 * @returns {string} - JST時刻文字列（例: "2026-02-01 14:09:13 JST"）
 */
export function formatJST(utcString) {
  if (!utcString) return ''
  const date = new Date(utcString)
  return date.toLocaleString('ja-JP', { 
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }) + ' JST'
}

/**
 * 秒数を "M:SS" 形式にフォーマット
 * @param {number} seconds
 * @returns {string}
 */
export function formatDuration(seconds) {
  if (!seconds) return '0:00'
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

/**
 * 残り秒数を "MM:SS" 形式にフォーマット（ロック残り時間表示用）
 * @param {number} seconds - 残り秒数（0未満は 0 として扱う）
 * @returns {string}
 */
export function formatRemainingTime(seconds) {
  if (seconds == null || seconds < 0) return '0:00'
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

/**
 * 待機時間（時間単位の小数）を人間向け文字列にフォーマット
 * @param {number} hours - 待機時間（例: 0.5 = 30分, 1.25 = 1時間15分）
 * @returns {string}
 */
export function formatWaitingTime(hours) {
  if (hours < 1) {
    const minutes = Math.floor(hours * 60)
    return `${minutes}分`
  }
  if (hours < 24) {
    const h = Math.floor(hours)
    const m = Math.floor((hours - h) * 60)
    return m > 0 ? `${h}時間${m}分` : `${h}時間`
  }
  const days = Math.floor(hours / 24)
  const remainder = hours - days * 24
  const h = Math.floor(remainder)
  const m = Math.floor((remainder - h) * 60)
  if (h > 0 && m > 0) return `${days}日${h}時間${m}分`
  if (h > 0) return `${days}日${h}時間`
  return `${days}日`
}

/**
 * タスク受付（ロック取得）を実行し、結果に応じてコールバックを呼ぶ
 * @param {Object} task - 受付対象タスク（id を持つ）
 * @param {Object} supabase - Supabase クライアント
 * @param {Object} callbacks
 * @param {(lockResult: { expires_at?: string } | null) => Promise<void>} callbacks.onSuccess - ロック取得成功時（期限 expires_at を渡す）
 * @param {() => void} callbacks.onConflict - 他講師に取られた時（23505 等）
 * @returns {Promise<{ success: boolean, conflict?: boolean }>}
 */
export async function acceptTaskWithLock(task, supabase, { onSuccess, onConflict }) {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) {
    throw new Error('認証が必要です')
  }

  const { data: lockResult, error: lockError } = await supabase.rpc(
    'set_feedback_lock_with_transaction',
    {
      p_submission_id: task.id,
      p_instructor_id: user.id,
      p_lock_duration_minutes: 15
    }
  )

  if (lockError) {
    // 競合: 他講師が先にロック済み（PostgreSQL UNIQUE違反 23505 / DBのメッセージ / HTTP 409）
    const isConflict =
      lockError.code === '23505' ||
      lockError.code === '409' ||
      lockError.status === 409 ||
      lockError.statusCode === 409 ||
      (lockError.message && lockError.message.includes('既に他の講師'))
    if (isConflict) {
      onConflict?.()
      return { success: false, conflict: true }
    }
    throw lockError
  }

  if (!lockResult || !lockResult.success) {
    throw new Error('ロックの取得に失敗しました')
  }

  await onSuccess?.(lockResult)
  return { success: true }
}

/**
 * モーダルを閉じるときにその提出のロックだけ解除する（未完了で閉じた場合用）
 * ※ finish_feedback は submission の status を feedback_completed にするため使わない
 * @param {string} submissionId - 提出 ID
 * @param {Object} supabase - Supabase クライアント
 * @returns {Promise<void>}
 */
export async function releaseFeedbackLock(submissionId, supabase) {
  if (!submissionId) {
    console.warn('⚠️ releaseFeedbackLock: submissionId が空です')
    return
  }
  
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) {
    console.warn('⚠️ releaseFeedbackLock: ユーザー認証なし')
    return
  }
  
  console.log('🔓 ロック解除開始:', { submissionId, instructorId: user.id })
  
  // release_feedback_lock RPC があればそれを使う（status を変えない）。なければ自ロック行を DELETE
  const { data: rpcData, error: rpcError } = await supabase.rpc('release_feedback_lock', {
    p_submission_id: submissionId,
    p_instructor_id: user.id
  })
  
  if (!rpcError) {
    console.log('✅ ロック解除成功（RPC）:', submissionId)
    return
  }
  
  // RPC が存在しない等の場合は、自講師のロック行だけ DELETE（RLS で許可されていれば成功）
  if (rpcError.code === '42883' || rpcError.message?.includes('does not exist')) {
    console.log('📝 RPC未実装、直接DELETEで解除:', submissionId)
    const { error: delError } = await supabase
      .from('feedback_locks')
      .delete()
      .eq('submission_id', submissionId)
      .eq('instructor_id', user.id)
    if (delError) {
      console.error('❌ 直接DELETE失敗:', delError)
      throw delError
    }
    console.log('✅ ロック解除成功（直接DELETE）:', submissionId)
    return
  }
  
  console.error('❌ ロック解除失敗（RPC エラー）:', rpcError)
  throw rpcError
}
